import React, { Fragment } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import {
  BaseText,
  AvatarImage,
  Icon,
  Separator,
  Touchable,
  TouchableTypeEnum,
  Button,
  ButtonStyleEnum,
  TextTruncated,
} from '@components';
import {
  PersonalizationNavigatorNavigationPropType,
  PersonalizationNavigatorRoutePropType,
  screens,
} from '@navigation';
import { IS_WEB, parseApiError } from '@utils';
import { ToastService } from '@services';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { useViewerModel } from '@models/Viewer/ViewerModel';

const PICTURE_SIZE = IS_WEB ? 120 : 100;

const Container = styled(View)({
  minHeight: [290, 160],
  px: ['$6', '$7', '5%'],
  flexDirection: ['column', 'row'],
  bg: '$background0',
});

const AvatarImageContainer = styled(View)({
  alignItems: 'center',
  pt: ['$0', '$4'],
});

const InfoContainer = styled(View)({
  ml: ['$0', '$7'],
  mt: ['$5', '$0'],
  flexGrow: 1,
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'center',
});

const InfoContentContainer = styled(View)({
  maxWidth: ['100%', 360],
});

const LocationContainer = styled(RowContainer)({
  justifyContent: ['center', 'flex-start'],
});

const UserStatisticContainer = styled(RowContainer)({
  justifyContent: ['space-around', 'space-between'],
});

const TouchableContainer = styled(Touchable)({
  flexDirection: ['column', 'row-reverse'],
  justifyContent: 'center',
  alignItems: 'center',
});

interface ITextStyleProps {
  lighter?: boolean;
}

const Text = styled(BaseText)((props: ITextStyleProps) => ({
  px: '$4',
  color: props.lighter ? '$text80' : '$text400',
  variant: 'text.small',
}));

const Title = styled(BaseText)((props: ITextStyleProps) => ({
  textAlign: ['center', 'left'],
  fontWeight: 'semi',
  variant: 'text.large',
  color: props.lighter ? '$text300' : '$heading800',
}));

const Description = styled(TextTruncated)({
  textAlign: ['center', 'left'],
  color: '$text400',
  variant: 'text.medium',
});

const ButtonsContainer = styled(RowContainer)({
  py: '$7',
});

const ButtonContainer = styled(View)({
  flex: 1,
});

// const SearchInputContainer = styled(View)({
//   display: ['none', null, null, 'flex'],
//   minWidth: 270,
// });

type RoutePropType =
  PersonalizationNavigatorRoutePropType<'UserProfile'>;

type NavigationPropType =
  PersonalizationNavigatorNavigationPropType<'UserProfile'>;

export const UserProfileHeader = observer(() => {
  const { t } = useTranslation();
  const { params } = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const viewer = useViewerModel();
  const item = viewer.getUserById(params.id);

  useNavOptionsSet({
    title: item?.fullName,
  });

  useAsyncInit(async () => {
    try {
      await viewer.fetchUserById.run(params.id);
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  async function toggleFollow() {
    try {
      await item?.toggleFollow.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function navigateToSendMessage() {
    // TODO: Add send message nav
  }

  function navigateToFollowers() {
    if (item) {
      nav.push(screens.Followers, {
        id: item.id,
      });
    }
  }

  function navigateToFollowing() {
    if (item) {
      nav.push(screens.Following, {
        id: item.id,
      });
    }
  }

  const buttonStyle = item?.isFollowing
    ? ButtonStyleEnum.Outline
    : ButtonStyleEnum.Flat;

  const buttonTitle = t('userProfile.button', {
    context: item?.isFollowing ? 'following' : 'follow',
  });

  const withMessageButton = !!params?.isShowMessageButton;
  const hasSharedData = !!item?.shared;
  const hasFollowers = !!item?.shared?.followers;
  const hasFollowing = !!item?.shared?.following;

  return (
    <Container>
      <AvatarImageContainer>
        <AvatarImage
          size={PICTURE_SIZE}
          source={{
            uri: item?.photo.big?.url,
          }}
        />
      </AvatarImageContainer>

      <InfoContainer>
        <InfoContentContainer>
          <Title numberOfLines={1}>{item?.fullName}</Title>

          {!!item?.description && (
            <Fragment>
              <Separator size="$4" />
              <Description numberOfLines={2}>
                {item?.description}
              </Description>
            </Fragment>
          )}

          {!!item?.location && (
            <Fragment>
              <Separator size="$4" />
              <LocationContainer>
                <Icon name="location-light" size={20} />
                <Text>{item?.location}</Text>
              </LocationContainer>
            </Fragment>
          )}

          <Separator />
          <UserStatisticContainer>
            <TouchableContainer
              disabled={!hasFollowers}
              type={TouchableTypeEnum.Opacity}
              onPress={navigateToFollowers}
            >
              <Text>{t('userProfile.followers')}</Text>
              <Title lighter={!hasFollowers}>
                {item?.shared?.followers}
              </Title>
            </TouchableContainer>

            <TouchableContainer
              disabled={!hasFollowing}
              type={TouchableTypeEnum.Opacity}
              onPress={navigateToFollowing}
            >
              <Text>{t('userProfile.following')}</Text>
              <Title lighter={!hasFollowing}>
                {item?.shared?.following}
              </Title>
            </TouchableContainer>
          </UserStatisticContainer>

          {!item?.isViewer && (
            <ButtonsContainer>
              <ButtonContainer>
                {hasSharedData && (
                  <Button
                    buttonStyle={buttonStyle}
                    onPress={toggleFollow}
                  >
                    {buttonTitle}
                  </Button>
                )}
              </ButtonContainer>

              {withMessageButton && (
                <Fragment>
                  <Separator size="$7" vertical />
                  <ButtonContainer>
                    <Button
                      buttonStyle={ButtonStyleEnum.Outline}
                      onPress={navigateToSendMessage}
                    >
                      {t('userProfile.message')}
                    </Button>
                  </ButtonContainer>
                </Fragment>
              )}
            </ButtonsContainer>
          )}
        </InfoContentContainer>
      </InfoContainer>

      {/* <SearchInputContainer>
        <TextInput
          hasClearButton
          placeholder={t('common.screenTopBar.search')}
          iconName="search-light"
        />
      </SearchInputContainer> */}
    </Container>
  );
});
